import React, { useState } from 'react';
import {
  Dialog,
  DialogContent,
  Typography,
  Box,
  Checkbox,
  FormControlLabel,
  IconButton,
} from '@mui/material';
import { Close as CloseIcon } from '@mui/icons-material';
import {
  WALNUT_WALKTHROUGH_URL,
  PREVIEW_BACKGROUND_IMAGE,
  ModalSizing,
  PreviewButton,
  ModalTypography,
  ModalColors,
  ImagePositioning,
  ModalZIndex,
} from '@constants/walnutConstants';

export interface WalnutWalkthroughModalProps {
  open: boolean;
  onClose: () => void;
  onDontShowAgain: (checked: boolean) => void;
}

const WalnutWalkthroughModal: React.FC<WalnutWalkthroughModalProps> = ({
  open,
  onClose,
  onDontShowAgain,
}) => {
  const [dontShowAgain, setDontShowAgain] = useState(false);

  const handleClose = () => {
    if (dontShowAgain) {
      onDontShowAgain(true);
    }
    onClose();
  };

  const handleCloseButtonClick = (event: React.MouseEvent) => {
    event.stopPropagation();
    handleClose();
  };

  const handleCheckboxChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setDontShowAgain(event.target.checked);
  };

  const handleOpenWalkthrough = () => {
    window.open(WALNUT_WALKTHROUGH_URL, '_blank', 'noopener,noreferrer');
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      slotProps={{
        paper: {
          elevation: 0,
          sx: {
            borderRadius: ModalSizing.borderRadius,
            border: ModalColors.border,
            width: { xs: '95%', sm: ModalSizing.width },
            maxWidth: ModalSizing.width,
            m: 2,
            position: 'relative',
            overflow: 'visible',
          },
        },
      }}
    >
      <DialogContent
        sx={{
          p: 0,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 3,
          pb: 4,
          position: 'relative',
          '&:last-child': { pb: 4 },
        }}
      >
        {/* Close Button */}
        <IconButton
          aria-label="close"
          onClick={handleCloseButtonClick}
          sx={{
            position: 'absolute',
            right: 17,
            top: 14,
            backgroundColor: ModalColors.textPrimary,
            color: 'white',
            width: ModalSizing.closeButtonSize,
            height: ModalSizing.closeButtonSize,
            borderRadius: ModalSizing.closeButtonRadius,
            zIndex: ModalZIndex.closeButton,
            p: 1,
            '&:hover': {
              backgroundColor: ModalColors.textPrimary,
              opacity: 0.9,
            },
          }}
        >
          <CloseIcon sx={{ fontSize: 24 }} />
        </IconButton>

        {/* Preview Section Container - creates white border effect */}
        <Box
          sx={{
            position: 'relative',
            width: '100%',
            aspectRatio: ModalSizing.previewAspectRatio,
            p: ModalSizing.previewPadding,
            boxSizing: 'border-box',
          }}
        >
          {/* Image Container */}
          <Box
            sx={{
              position: 'relative',
              width: '100%',
              height: '100%',
              borderRadius: ModalSizing.borderRadius,
              overflow: 'hidden',
              cursor: 'pointer',
            }}
            onClick={handleOpenWalkthrough}
          >
            {/* Background Image */}
            <Box
              component="img"
              src={PREVIEW_BACKGROUND_IMAGE}
              alt="Portal Preview"
              sx={{
                position: 'absolute',
                width: ImagePositioning.width,
                height: ImagePositioning.height,
                left: ImagePositioning.left,
                top: 0,
                objectFit: 'cover',
                opacity: ImagePositioning.opacity,
              }}
            />
            {/* Dark Overlay */}
            <Box
              sx={{
                position: 'absolute',
                inset: 0,
                backgroundColor: ModalColors.imageOverlay,
                borderRadius: ModalSizing.borderRadius,
              }}
            />

            {/* See Preview Button */}
            <Box
              sx={{
                position: 'absolute',
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
                backgroundColor: ModalColors.previewButtonBg,
                border: PreviewButton.border,
                borderRadius: PreviewButton.borderRadius,
                px: {
                  xs: PreviewButton.paddingXMobile,
                  md: PreviewButton.paddingX,
                },
                py: {
                  xs: PreviewButton.paddingYMobile,
                  md: PreviewButton.paddingY,
                },
                display: 'flex',
                alignItems: 'center',
                gap: PreviewButton.gap,
                cursor: 'pointer',
                '&:hover': {
                  backgroundColor: ModalColors.previewButtonBgHover,
                },
              }}
            >
              {/* Play Icon */}
              <Box
                sx={{
                  width: {
                    xs: PreviewButton.iconSizeMobile,
                    md: PreviewButton.iconSize,
                  },
                  height: {
                    xs: PreviewButton.iconSizeMobile,
                    md: PreviewButton.iconSize,
                  },
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <Box
                  component="svg"
                  viewBox="0 0 48 48"
                  sx={{
                    width: {
                      xs: PreviewButton.iconSizeMobile,
                      md: PreviewButton.iconSize,
                    },
                    height: {
                      xs: PreviewButton.iconSizeMobile,
                      md: PreviewButton.iconSize,
                    },
                    transform: 'rotate(90deg)',
                  }}
                >
                  <polygon
                    points="24,8 40,40 8,40"
                    fill={ModalColors.textWhite}
                  />
                </Box>
              </Box>
              <Typography
                sx={{
                  color: ModalColors.textWhite,
                  fontFamily: '"Roboto", sans-serif',
                  fontWeight: 600,
                  fontSize: {
                    xs: ModalTypography.previewTextSizeMobile,
                    md: ModalTypography.previewTextSizeDesktop,
                  },
                  textShadow: '0px 4px 4px rgba(0, 0, 0, 0.25)',
                  whiteSpace: 'nowrap',
                  lineHeight: 'normal',
                }}
              >
                See Preview
              </Typography>
            </Box>
          </Box>
        </Box>

        {/* Title */}
        <Typography
          sx={{
            fontFamily: '"Roboto", sans-serif',
            fontWeight: 600,
            fontSize: {
              xs: ModalTypography.titleSizeMobile,
              md: ModalTypography.titleSizeDesktop,
            },
            color: ModalColors.textPrimary,
            textAlign: 'center',
            lineHeight: 'normal',
            px: 3,
          }}
        >
          Introducing New Online Portals!
        </Typography>

        {/* Description */}
        <Typography
          sx={{
            fontFamily: '"Roboto", sans-serif',
            fontWeight: 400,
            fontSize: {
              xs: ModalTypography.descriptionSizeMobile,
              md: ModalTypography.descriptionSizeDesktop,
            },
            color: ModalColors.textSecondary,
            textAlign: 'center',
            lineHeight: 1.5,
            px: 4,
          }}
        >
          Check out our new Borrower Portals with the interactive walk-through
          above.
        </Typography>

        {/* Divider Line */}
        <Box
          sx={{
            width: ModalSizing.dividerWidth,
            height: 1,
            backgroundColor: ModalColors.divider,
          }}
        />

        {/* Checkbox */}
        <FormControlLabel
          control={
            <Checkbox
              checked={dontShowAgain}
              onChange={handleCheckboxChange}
              sx={{
                width: ModalTypography.checkboxSize,
                height: ModalTypography.checkboxSize,
                color: ModalColors.textMuted,
                '&.Mui-checked': {
                  color: ModalColors.textMuted,
                },
                '& .MuiSvgIcon-root': {
                  fontSize: ModalTypography.checkboxSize,
                },
              }}
            />
          }
          label={
            <Typography
              sx={{
                fontFamily: '"Roboto", sans-serif',
                fontWeight: 400,
                fontSize: ModalTypography.checkboxLabelSize,
                color: ModalColors.textSecondary,
                lineHeight: 1.5,
              }}
            >
              Don't show this message again
            </Typography>
          }
          sx={{
            gap: 1,
            m: 0,
          }}
        />
      </DialogContent>
    </Dialog>
  );
};

export default WalnutWalkthroughModal;
